import React, { useState, useMemo, useEffect } from 'react';
import Swal from 'sweetalert2';
import Select from 'react-select';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';

const Batches = () => {
    const [batches, setBatches] = useState([]);
    const [courses, setCourses] = useState([]);
    const [accessibleCourseIds, setAccessibleCourseIds] = useState([]);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [stats, setStats] = useState({
        total: 0,
        notStarted: 0,
        ongoing: 0,
        deliveryCompleted: 0,
        batchCompleted: 0,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // State Management
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [selectedBatch, setSelectedBatch] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [courseFilter, setCourseFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    
    // Form States
    const [formData, setFormData] = useState({
        courseId: '',
        batchName: '',
        batchInternalName: '',
        registrationOpeningDate: '',
        registrationClosingDate: '',
        batchStartingDate: '',
        batchEndingDate: '',
        whatsappGroupName: '',
        whatsapp_group_id: '',
        description: '',
        status: 'not started',
    });
    
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const ADMIN_PREFIX = '/admin';

    const loadBatches = async () => {
        setLoading(true);
        setError('');
        try {
            const resp = await axiosInstance.get(`${ADMIN_PREFIX}/batches`, {
                params: {
                    courseId: courseFilter,
                    status: statusFilter,
                    search: searchQuery,
                },
            });
            const data = resp.data || {};
            setBatches(data.batches || []);
            setCourses(data.courses || []);
            if (data.stats) {
                setStats({
                    total: data.stats.total || 0,
                    notStarted: data.stats.notStarted || 0,
                    ongoing: data.stats.ongoing || 0,
                    deliveryCompleted: data.stats.deliveryCompleted || 0,
                    batchCompleted: data.stats.batchCompleted || 0,
                });
            }
        } catch (err) {
            console.error('Failed to load batches', err);
            setError(err.response?.data?.message || 'Failed to load batches');
            setBatches([]);
            setCourses([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            loadBatches();
        }, 300);

        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, courseFilter, statusFilter]);

    useEffect(() => {
        let cancelled = false;

        const loadAccess = async () => {
            try {
                const resp = await axiosInstance.get('/admin/me/coordinate-courses');
                if (!resp?.data || cancelled) return;
                const data = resp.data;
                const idsRaw = Array.isArray(data.accessibleCourseIds) ? data.accessibleCourseIds : [];
                const ids = idsRaw
                    .map((id) => {
                        const num = Number(id);
                        return Number.isFinite(num) ? num : null;
                    })
                    .filter((id) => id !== null);
                setAccessibleCourseIds(ids);
                setIsSuperAdmin(Boolean(data.isSuperAdmin));
            } catch (err) {
                if (!cancelled) {
                    setAccessibleCourseIds([]);
                    setIsSuperAdmin(false);
                }
            }
        };

        loadAccess();

        return () => {
            cancelled = true;
        };
    }, []);

    // Status badge color helper
    const getStatusBadgeColor = (status) => {
        switch (status) {
            case 'not started':
                return 'bg-gray-100 text-gray-800';
            case 'ongoing':
                return 'bg-blue-100 text-blue-800';
            case 'delivery completed':
                return 'bg-green-100 text-green-800';
            case 'batch completed':
                return 'bg-purple-100 text-purple-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const visibleCourses = useMemo(() => {
        if (isSuperAdmin) {
            return courses;
        }
        const allowed = new Set(accessibleCourseIds);
        if (!allowed.size) {
            return [];
        }
        return courses.filter((course) => allowed.has(course.courseId));
    }, [courses, accessibleCourseIds, isSuperAdmin]);


    // Prepare options for react-select course filter
    const courseSelectOptions = useMemo(() => {
        const options = visibleCourses.map(course => ({
            value: course.courseId.toString(),
            label: `${course.shortName} - ${course.fullName}`
        }));
        options.unshift({ value: 'all', label: 'All Courses' });
        return options;
    }, [visibleCourses]);

    // Prepare options for react-select status filter
    const statusSelectOptions = useMemo(() => {
        return [
            { value: 'all', label: 'All Statuses' },
            { value: 'not started', label: 'Not Started' },
            { value: 'ongoing', label: 'Ongoing' },
            { value: 'delivery completed', label: 'Delivery Completed' },
            { value: 'batch completed', label: 'Batch Completed' },
        ];
    }, []);

    // Custom styles for react-select (Course Filter - Indigo theme)
    const courseSelectStyles = {
        control: (base, state) => ({
            ...base,
            borderRadius: '0.5rem',
            borderWidth: '1px',
            borderColor: state.isFocused ? '#6366f1' : '#d1d5db',
            boxShadow: state.isFocused ? '0 0 0 3px rgba(99, 102, 241, 0.1)' : 'none',
            '&:hover': {
                borderColor: '#a5b4fc',
            },
            minHeight: '42px',
            paddingLeft: '4px',
            paddingRight: '4px',
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected 
                ? '#6366f1' 
                : state.isFocused 
                ? '#e0e7ff' 
                : 'white',
            color: state.isSelected ? 'white' : '#111827',
            fontWeight: state.isSelected ? '500' : '400',
            '&:hover': {
                backgroundColor: state.isSelected ? '#6366f1' : '#e0e7ff',
            },
        }),
        menu: (base) => ({
            ...base,
            borderRadius: '0.5rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            marginTop: '4px',
            zIndex: 50,
        }),
        singleValue: (base) => ({
            ...base,
            fontWeight: '500',
            fontSize: '0.875rem',
        }),
    };

    // Custom styles for react-select (Status Filter - Blue theme)
    const statusSelectStyles = {
        control: (base, state) => ({
            ...base,
            borderRadius: '0.5rem',
            borderWidth: '1px',
            borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
            boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
            '&:hover': {
                borderColor: '#93c5fd',
            },
            minHeight: '42px',
            paddingLeft: '4px',
            paddingRight: '4px',
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected 
                ? '#3b82f6' 
                : state.isFocused 
                ? '#dbeafe' 
                : 'white',
            color: state.isSelected ? 'white' : '#111827',
            fontWeight: state.isSelected ? '500' : '400',
            '&:hover': {
                backgroundColor: state.isSelected ? '#3b82f6' : '#dbeafe',
            },
        }),
        menu: (base) => ({
            ...base,
            borderRadius: '0.5rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            marginTop: '4px',
            zIndex: 50,
        }),
        singleValue: (base) => ({
            ...base,
            fontWeight: '500',
            fontSize: '0.875rem',
        }),
    };

    // Prepare options for react-select course in modals (without "All Courses")
    const courseModalOptions = useMemo(() => {
        return visibleCourses.map(course => ({
            value: course.courseId.toString(),
            label: `${course.shortName} - ${course.fullName}${course.scheduleType ? ` (${course.scheduleType})` : ''}`
        }));
    }, [visibleCourses]);

    // Prepare options for react-select status in modals (without "All Statuses")
    const statusModalOptions = useMemo(() => {
        return [
            { value: 'not started', label: 'Not Started' },
            { value: 'ongoing', label: 'Ongoing' },
            { value: 'delivery completed', label: 'Delivery Completed' },
            { value: 'batch completed', label: 'Batch Completed' },
        ];
    }, []);

    // Custom styles for react-select in modals (Course - Indigo theme with error states)
    const courseModalSelectStyles = {
        control: (base, state) => ({
            ...base,
            borderRadius: '0.5rem',
            borderWidth: '1px',
            borderColor: errors.courseId 
                ? '#ef4444' 
                : state.isFocused 
                ? '#6366f1' 
                : '#d1d5db',
            boxShadow: state.isFocused ? '0 0 0 3px rgba(99, 102, 241, 0.1)' : 'none',
            '&:hover': {
                borderColor: errors.courseId ? '#ef4444' : '#a5b4fc',
            },
            minHeight: '42px',
            paddingLeft: '4px',
            paddingRight: '4px',
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected 
                ? '#6366f1' 
                : state.isFocused 
                ? '#e0e7ff' 
                : 'white',
            color: state.isSelected ? 'white' : '#111827',
            fontWeight: state.isSelected ? '500' : '400',
            '&:hover': {
                backgroundColor: state.isSelected ? '#6366f1' : '#e0e7ff',
            },
        }),
        menu: (base) => ({
            ...base,
            borderRadius: '0.5rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            marginTop: '4px',
            zIndex: 9999,
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
        }),
        singleValue: (base) => ({
            ...base,
            fontWeight: '500',
            fontSize: '0.875rem',
        }),
        placeholder: (base) => ({
            ...base,
            fontSize: '0.875rem',
        }),
    };

    // Custom styles for react-select in modals (Status - Blue theme with error states)
    const statusModalSelectStyles = {
        control: (base, state) => ({
            ...base,
            borderRadius: '0.5rem',
            borderWidth: '1px',
            borderColor: errors.status 
                ? '#ef4444' 
                : state.isFocused 
                ? '#3b82f6' 
                : '#d1d5db',
            boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
            '&:hover': {
                borderColor: errors.status ? '#ef4444' : '#93c5fd',
            },
            minHeight: '42px',
            paddingLeft: '4px',
            paddingRight: '4px',
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected 
                ? '#3b82f6' 
                : state.isFocused 
                ? '#dbeafe' 
                : 'white',
            color: state.isSelected ? 'white' : '#111827',
            fontWeight: state.isSelected ? '500' : '400',
            '&:hover': {
                backgroundColor: state.isSelected ? '#3b82f6' : '#dbeafe',
            },
        }),
        menu: (base) => ({
            ...base,
            borderRadius: '0.5rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            marginTop: '4px',
            zIndex: 9999,
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
        }),
        singleValue: (base) => ({
            ...base,
            fontWeight: '500',
            fontSize: '0.875rem',
        }),
        placeholder: (base) => ({
            ...base,
            fontSize: '0.875rem',
        }),
    };

    // Form Validation
    const validateForm = async (data, isEdit = false) => {
        const newErrors = {};

        // Course validation
        if (!data.courseId) {
            newErrors.courseId = 'Course is required';
        }

        // Batch name validation
        if (!data.batchName.trim()) {
            newErrors.batchName = 'Batch name is required';
        } else if (data.batchName.length > 255) {
            newErrors.batchName = 'Batch name must not exceed 255 characters';
        } else {
            // Check uniqueness
            try {
                const response = await axiosInstance.get(`${ADMIN_PREFIX}/batches/check-name`, {
                    params: {
                        batchName: data.batchName,
                        courseId: data.courseId,
                        ...(isEdit && selectedBatch ? { excludeId: selectedBatch.batchId } : {}),
                    },
                });
                if (response.data?.exists) {
                    newErrors.batchName = 'This batch name already exists for the selected course';
                }
            } catch (err) {
                console.error('Failed to check batch name', err);
            }
        }

        // Date validations
        if (!data.registrationOpeningDate) {
            newErrors.registrationOpeningDate = 'Registration opening date is required';
        }

        if (!data.registrationClosingDate) {
            newErrors.registrationClosingDate = 'Registration closing date is required';
        } else if (data.registrationOpeningDate && new Date(data.registrationClosingDate) < new Date(data.registrationOpeningDate)) {
            newErrors.registrationClosingDate = 'Registration closing date must be after opening date';
        }

        if (!data.batchStartingDate) {
            newErrors.batchStartingDate = 'Batch starting date is required';
        }

        if (!data.batchEndingDate) {
            newErrors.batchEndingDate = 'Batch ending date is required';
        } else if (data.batchStartingDate && new Date(data.batchEndingDate) <= new Date(data.batchStartingDate)) {
            newErrors.batchEndingDate = 'Batch ending date must be after starting date';
        }

        if (!data.status) {
            newErrors.status = 'Status is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle Add Modal
    const openAddModal = () => {
        setFormData({
            courseId: '',
            batchName: '',
            batchInternalName: '',
            registrationOpeningDate: '',
            registrationClosingDate: '',
            batchStartingDate: '',
            batchEndingDate: '',
            whatsappGroupName: '',
            whatsapp_group_id: '',
            description: '',
            status: 'not started',
        });
        setErrors({});
        setIsAddModalOpen(true);
    };

    const closeAddModal = () => {
        setIsAddModalOpen(false);
        setFormData({
            courseId: '',
            batchName: '',
            batchInternalName: '',
            registrationOpeningDate: '',
            registrationClosingDate: '',
            batchStartingDate: '',
            batchEndingDate: '',
            whatsappGroupName: '',
            whatsapp_group_id: '',
            description: '',
            status: 'not started',
        });
        setErrors({});
    };

    // Handle Edit Modal
    const openEditModal = (batch) => {
        setSelectedBatch(batch);
        setFormData({
            courseId: batch.courseId.toString(),
            batchName: batch.batchName,
            batchInternalName: batch.batchInternalName || '',
            registrationOpeningDate: batch.registrationOpeningDate.split('T')[0],
            registrationClosingDate: batch.registrationClosingDate.split('T')[0],
            batchStartingDate: batch.batchStartingDate.split('T')[0],
            batchEndingDate: batch.batchEndingDate.split('T')[0],
            whatsappGroupName: batch.whatsappGroupName || '',
            whatsapp_group_id: batch.whatsapp_group_id || '',
            description: batch.description || '',
            status: batch.status,
        });
        setErrors({});
        setIsEditModalOpen(true);
    };

    const closeEditModal = () => {
        setIsEditModalOpen(false);
        setSelectedBatch(null);
        setFormData({
            courseId: '',
            batchName: '',
            batchInternalName: '',
            registrationOpeningDate: '',
            registrationClosingDate: '',
            batchStartingDate: '',
            batchEndingDate: '',
            whatsappGroupName: '',
            whatsapp_group_id: '',
            description: '',
            status: 'not started',
        });
        setErrors({});
    };

    // Handle Details Modal
    const openDetailsModal = (batch) => {
        setSelectedBatch(batch);
        setIsDetailsModalOpen(true);
    };

    const closeDetailsModal = () => {
        setIsDetailsModalOpen(false);
        setSelectedBatch(null);
    };

    // Handle Form Submit
    const handleSubmit = async (e, isEdit = false) => {
        e.preventDefault();
        
        const isValid = await validateForm(formData, isEdit);
        if (!isValid) return;

        setIsSubmitting(true);

        try {
            if (isEdit && selectedBatch) {
                await axiosInstance.put(`${ADMIN_PREFIX}/batches/${selectedBatch.batchId}`, formData);
                Swal.fire('Updated', 'Batch updated successfully.', 'success');
                closeEditModal();
            } else {
                await axiosInstance.post(`${ADMIN_PREFIX}/batches`, formData);
                Swal.fire('Created', 'Batch created successfully.', 'success');
                closeAddModal();
            }
            await loadBatches();
        } catch (err) {
            console.error('Failed to save batch', err);
            const msg = err.response?.data?.error || err.response?.data?.message || 'Failed to save batch';
            Swal.fire('Error', msg, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle Delete
    const handleDelete = (batch) => {
        Swal.fire({
            title: 'Are you sure?',
            text: `Do you want to delete batch "${batch.batchName}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, delete it!',
        }).then((result) => {
            if (result.isConfirmed) {
                axiosInstance.delete(`${ADMIN_PREFIX}/batches/${batch.batchId}`)
                    .then(() => {
                        Swal.fire('Deleted', 'Batch deleted successfully.', 'success');
                        loadBatches();
                    })
                    .catch((err) => {
                        console.error('Failed to delete batch', err);
                        const msg = err.response?.data?.error || err.response?.data?.message || 'Failed to delete batch';
                        Swal.fire('Error', msg, 'error');
                    });
            }
        });
    };

    // Handle Input Change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        // Clear error for this field
        if (errors[name]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    // Handle React Select Change for Course
    const handleCourseChange = (selectedOption) => {
        setFormData((prev) => ({ ...prev, courseId: selectedOption?.value || '' }));
        // Clear error for courseId
        if (errors.courseId) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors.courseId;
                return newErrors;
            });
        }
    };

    // Handle React Select Change for Status
    const handleStatusChange = (selectedOption) => {
        setFormData((prev) => ({ ...prev, status: selectedOption?.value || 'not started' }));
        // Clear error for status
        if (errors.status) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors.status;
                return newErrors;
            });
        }
    };

    return (
        <DashboardLayout title="Batches">

            <div className="p-6">
                {error && (
                    <div className="mb-4 rounded-md bg-red-50 p-4 text-sm text-red-700">
                        {error}
                    </div>
                )}
                {/* Header */}
                <div className="mb-6 flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">Batches</h1>
                        <p className="text-gray-600 mt-1">Manage course batches and schedules</p>
                    </div>
                    <button
                        onClick={openAddModal}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
                    >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Batch
                    </button>
                </div>

                {/* Statistics */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="text-sm text-gray-600">Total Batches</div>
                        <div className="text-3xl font-bold text-indigo-600">{stats.total}</div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="text-sm text-gray-600">Not Started</div>
                        <div className="text-3xl font-bold text-gray-600">{stats.notStarted}</div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="text-sm text-gray-600">Ongoing</div>
                        <div className="text-3xl font-bold text-blue-600">{stats.ongoing}</div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="text-sm text-gray-600">Delivery Completed</div>
                        <div className="text-3xl font-bold text-green-600">{stats.deliveryCompleted}</div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="text-sm text-gray-600">Batch Completed</div>
                        <div className="text-3xl font-bold text-purple-600">{stats.batchCompleted}</div>
                    </div>
                </div>

                {/* Search and Filter */}
                <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search batches..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                        <svg
                            className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                        </svg>
                    </div>
                    <Select
                        options={courseSelectOptions}
                        value={courseSelectOptions.find(option => option.value === courseFilter)}
                        onChange={(option) => setCourseFilter(option?.value || 'all')}
                        styles={courseSelectStyles}
                        placeholder="Filter by Course..."
                        isClearable={false}
                        isSearchable={true}
                    />
                    <Select
                        options={statusSelectOptions}
                        value={statusSelectOptions.find(option => option.value === statusFilter)}
                        onChange={(option) => setStatusFilter(option?.value || 'all')}
                        styles={statusSelectStyles}
                        placeholder="Filter by Status..."
                        isClearable={false}
                        isSearchable={false}
                    />
                </div>

                {/* Batches Table */}
                <div className="bg-white rounded-lg shadow overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    #
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Batch ID
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Batch Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Start Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    End Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {batches.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                                        No batches found
                                    </td>
                                </tr>
                            ) : (
                                batches.map((batch, index) => (
                                    <tr key={batch.batchId} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {index + 1}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {batch.batchId}
                                        </td>
                                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                            {batch.batchName}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                            {new Date(batch.batchStartingDate).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                            {new Date(batch.batchEndingDate).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(batch.status)}`}>
                                                {batch.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                            <button
                                                onClick={() => openDetailsModal(batch)}
                                                className="text-blue-600 hover:text-blue-900 mr-3"
                                            >
                                                View
                                            </button>
                                            <button
                                                onClick={() => openEditModal(batch)}
                                                className="text-indigo-600 hover:text-indigo-900 mr-3"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDelete(batch)}
                                                className="text-red-600 hover:text-red-900"
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Add/Edit Modal */}
                {(isAddModalOpen || isEditModalOpen) && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                            <div className="p-6 border-b border-gray-200 sticky top-0 bg-white">
                                <h2 className="text-xl font-bold text-gray-800">
                                    {isEditModalOpen ? 'Edit Batch' : 'Add New Batch'}
                                </h2>
                            </div>
                            <form onSubmit={(e) => handleSubmit(e, isEditModalOpen)}>
                                <div className="p-6 space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Course Selection */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Course <span className="text-red-500">*</span>
                                            </label>
                                            <Select
                                                options={courseModalOptions}
                                                value={courseModalOptions.find(option => option.value === formData.courseId) || null}
                                                onChange={handleCourseChange}
                                                styles={courseModalSelectStyles}
                                                placeholder="Select Course..."
                                                isClearable={false}
                                                isSearchable={true}
                                                menuPortalTarget={document.body}
                                            />
                                            {errors.courseId && (
                                                <p className="mt-1 text-sm text-red-500">{errors.courseId}</p>
                                            )}
                                        </div>

                                        {/* Status */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Status <span className="text-red-500">*</span>
                                            </label>
                                            <Select
                                                options={statusModalOptions}
                                                value={statusModalOptions.find(option => option.value === formData.status) || null}
                                                onChange={handleStatusChange}
                                                styles={statusModalSelectStyles}
                                                placeholder="Select Status..."
                                                isClearable={false}
                                                isSearchable={false}
                                                menuPortalTarget={document.body}
                                            />
                                            {errors.status && (
                                                <p className="mt-1 text-sm text-red-500">{errors.status}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Batch Name */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Batch Name <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="batchName"
                                                value={formData.batchName}
                                                onChange={handleInputChange}
                                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                                                    errors.batchName ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                                placeholder="e.g., Batch 2024-01"
                                            />
                                            {errors.batchName && (
                                                <p className="mt-1 text-sm text-red-500">{errors.batchName}</p>
                                            )}
                                        </div>

                                        {/* Batch Internal Name */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Internal Name
                                            </label>
                                            <input
                                                type="text"
                                                name="batchInternalName"
                                                value={formData.batchInternalName}
                                                onChange={handleInputChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                                placeholder="Internal reference name"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Registration Opening Date */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Registration Opening <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="date"
                                                name="registrationOpeningDate"
                                                value={formData.registrationOpeningDate}
                                                onChange={handleInputChange}
                                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                                                    errors.registrationOpeningDate ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            />
                                            {errors.registrationOpeningDate && (
                                                <p className="mt-1 text-sm text-red-500">{errors.registrationOpeningDate}</p>
                                            )}
                                        </div>

                                        {/* Registration Closing Date */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Registration Closing <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="date"
                                                name="registrationClosingDate"
                                                value={formData.registrationClosingDate}
                                                onChange={handleInputChange}
                                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                                                    errors.registrationClosingDate ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            />
                                            {errors.registrationClosingDate && (
                                                <p className="mt-1 text-sm text-red-500">{errors.registrationClosingDate}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Batch Starting Date */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Batch Start Date <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="date"
                                                name="batchStartingDate"
                                                value={formData.batchStartingDate}
                                                onChange={handleInputChange}
                                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                                                    errors.batchStartingDate ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            />
                                            {errors.batchStartingDate && (
                                                <p className="mt-1 text-sm text-red-500">{errors.batchStartingDate}</p>
                                            )}
                                        </div>

                                        {/* Batch Ending Date */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Batch End Date <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="date"
                                                name="batchEndingDate"
                                                value={formData.batchEndingDate}
                                                onChange={handleInputChange}
                                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                                                    errors.batchEndingDate ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            />
                                            {errors.batchEndingDate && (
                                                <p className="mt-1 text-sm text-red-500">{errors.batchEndingDate}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* WhatsApp Group Name */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                WhatsApp Group Name
                                            </label>
                                            <input
                                                type="text"
                                                name="whatsappGroupName"
                                                value={formData.whatsappGroupName}
                                                onChange={handleInputChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                                placeholder="WhatsApp group name"
                                            />
                                        </div>

                                        {/* WhatsApp Group ID */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                WhatsApp Group ID
                                            </label>
                                            <input
                                                type="text"
                                                name="whatsapp_group_id"
                                                value={formData.whatsapp_group_id}
                                                onChange={handleInputChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                                placeholder="WhatsApp group ID"
                                            />
                                        </div>
                                    </div>

                                    {/* Description */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Description
                                        </label>
                                        <textarea
                                            name="description"
                                            value={formData.description}
                                            onChange={handleInputChange}
                                            rows={3}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                            placeholder="Additional notes or description"
                                        />
                                    </div>
                                </div>
                                <div className="p-6 border-t border-gray-200 flex justify-end gap-2 sticky bottom-0 bg-white">
                                    <button
                                        type="button"
                                        onClick={isEditModalOpen ? closeEditModal : closeAddModal}
                                        disabled={isSubmitting}
                                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                                    >
                                        {isSubmitting ? (isEditModalOpen ? 'Updating...' : 'Creating...') : (isEditModalOpen ? 'Update Batch' : 'Create Batch')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Details Modal */}
                {isDetailsModalOpen && selectedBatch && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                            <div className="p-6 border-b border-gray-200 sticky top-0 bg-white">
                                <h2 className="text-xl font-bold text-gray-800">Batch Details</h2>
                            </div>
                            <div className="p-6 space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600">Batch Name</label>
                                        <p className="mt-1 text-lg font-semibold text-gray-900">{selectedBatch.batchName}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600">Course</label>
                                        <p className="mt-1 text-gray-900">{selectedBatch.course?.shortName} - {selectedBatch.course?.fullName}</p>
                                    </div>
                                </div>

                                {selectedBatch.batchInternalName && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600">Internal Name</label>
                                        <p className="mt-1 text-gray-900">{selectedBatch.batchInternalName}</p>
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium text-gray-600">Status</label>
                                    <p className="mt-1">
                                        <span className={`px-3 py-1 text-sm font-semibold rounded-full ${getStatusBadgeColor(selectedBatch.status)}`}>
                                            {selectedBatch.status}
                                        </span>
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600">Registration Opening</label>
                                        <p className="mt-1 text-gray-900">
                                            {new Date(selectedBatch.registrationOpeningDate).toLocaleDateString()}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600">Registration Closing</label>
                                        <p className="mt-1 text-gray-900">
                                            {new Date(selectedBatch.registrationClosingDate).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600">Batch Start Date</label>
                                        <p className="mt-1 text-gray-900">
                                            {new Date(selectedBatch.batchStartingDate).toLocaleDateString()}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600">Batch End Date</label>
                                        <p className="mt-1 text-gray-900">
                                            {new Date(selectedBatch.batchEndingDate).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>

                                {selectedBatch.whatsappGroupName && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600">WhatsApp Group Name</label>
                                        <p className="mt-1 text-gray-900">{selectedBatch.whatsappGroupName}</p>
                                    </div>
                                )}

                                {selectedBatch.whatsapp_group_id && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600">WhatsApp Group ID</label>
                                        <p className="mt-1 text-gray-900">{selectedBatch.whatsapp_group_id}</p>
                                    </div>
                                )}

                                {selectedBatch.description && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600">Description</label>
                                        <p className="mt-1 text-gray-900">{selectedBatch.description}</p>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600">Created At</label>
                                        <p className="mt-1 text-gray-900">
                                            {new Date(selectedBatch.createdAt).toLocaleString()}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600">Last Updated</label>
                                        <p className="mt-1 text-gray-900">
                                            {new Date(selectedBatch.updatedAt).toLocaleString()}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="p-6 border-t border-gray-200 flex justify-end sticky bottom-0 bg-white">
                                <button
                                    onClick={closeDetailsModal}
                                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default Batches;
